import { Button, Loading, Modal } from "@landtechnologies/components";
import { useState } from "react";
import { useSiteById } from "react-migration/domains/sites/hooks";
import { SiteReportUtilitySteps } from "react-migration/domains/sites/typings/SiteUtilityReport";
import {
  LandTechEndpoints,
  Stores,
  formatToCurrency,
  routedClient,
  useGlobalState,
  useTranslation,
} from "react-migration/lib";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";
import { setActiveSiteReportUtilityStep } from "src/js/stores/site/actions/setActiveSiteReportUtilityStep";
import { PlaceAtkinsOrderResponseData } from "../../../typings";
import { useMutation } from "@apollo/client";
import PLACE_ATKINS_ORDER from "react-migration/domains/sites/apollo/sites-api/mutations/placeAtkinsOrder.gql";
import { logUtilitiesReportEvent } from "./logUtilitiesEvent";

type ConfirmPaymentModalProps = {
  onClose: () => void;
};

export const ConfirmPaymentModal = ({ onClose }: ConfirmPaymentModalProps) => {
  const { t } = useTranslation();
  const [{ locale }] = useGlobalState(Stores.user);
  const [{ selection }] = useGlobalState(Stores.map);
  const [{ utilityGeometry }] = useGlobalState(Stores.site);
  const siteId = selection?.id;
  const { site } = useSiteById(siteId);
  const [loading, setLoading] = useState<boolean>(false);

  const [placeAtkinsOrder] = useMutation<PlaceAtkinsOrderResponseData>(PLACE_ATKINS_ORDER, {
    variables: {
      atkinsOrderInput: {
        siteId,
        geometry: utilityGeometry,
      },
    },
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    fetchPolicy: "no-cache",
  });

  const onConfirmPayment = async () => {
    setLoading(true);
    logUtilitiesReportEvent({
      name: "Utilities Report - Confirm Payment Button Clicked",
    });
    try {
      await placeAtkinsOrder();
      setActiveSiteReportUtilityStep(SiteReportUtilitySteps.Success);
    } catch (error) {
      setActiveSiteReportUtilityStep(SiteReportUtilitySteps.Error);
    }
  };

  const onCloseModalClick = () => {
    logUtilitiesReportEvent({
      name: "Utilities Report - Close Payment Button Clicked",
    });
    onClose();
  };

  const onCancelClick = () => {
    logUtilitiesReportEvent({
      name: "Utilities Report - Cancel Payment Button Clicked",
    });
    onClose();
  };

  const onContactClick = () => {
    window.open("mailto: support@land.tech?subject=Utilities report", "_blank");
  };

  return (
    <Modal contentClasses="atlas-bg-background-white !atlas-p-0 !atlas-z-100">
      <div className="atlas-bg-background-light atlas-border-b atlas-border-border-divider !atlas-rounded-t-md">
        <div className="atlas-p-3 atlas-flex atlas-flex-row atlas-justify-between atlas-items-center">
          <span className="atlas-font-semibold atlas-text-base atlas-leading-6">
            {loading
              ? t("sites.utility_report.we_are_processing_your_payment")
              : t("sites.utility_report.confirm_payment")}
          </span>
          <Button.Borderless
            className="atlas-px-0 atlas-h-6"
            onClick={onCloseModalClick}
            disabled={loading}
          >
            <i className="icon-lt-close-line atlas-text-2xl" />
          </Button.Borderless>
        </div>
      </div>

      {loading && (
        <div className="atlas-flex atlas-flex-col atlas-gap-y-3 atlas-mb-3 atlas-w-full atlas-h-28 atlas-justify-center atlas-items-center">
          <span className="atlas-flex atlas-text-sm atlas-font-semibold">
            {t("sites.utility_report.please_wait")}
          </span>
          <Loading />
        </div>
      )}

      {!loading && (
        <>
          <div className="atlas-pt-5 atlas-px-5 atlas-flex atlas-flex-col atlas-w-full atlas-gap-y-2 atlas-text-sm atlas-leading-5">
            <div>
              <span>
                {t(
                  "sites.utility_report.you_are_about_to_pay_amount_to_purchase_product_for_site",
                  {
                    amount: (
                      <strong>{formatToCurrency({ amount: 720, currency: "GBP", locale })}</strong>
                    ),
                    productName: <strong>{t("sites.utility_report.title")}</strong>,
                  }
                )}
              </span>
              <Button.Base
                buttonType="Anchor"
                className="atlas-text-content-link atlas-contents"
                onClick={() => router.push({ name: ROUTES.SITES, query: { site_id: siteId } })}
                data-testid="utilities-report-confirm-payment-modal-site-title"
              >
                {site?.title}
              </Button.Base>
            </div>
            <span className="atlas-flex">
              {t("sites.utility_report.this_payment_will_be_deducted_from_your_LandTech_balance")}
            </span>

            <div className="atlas-border-b atlas-border-border-divider-subtle atlas-mt-3" />

            <span className="atlas-py-1 atlas-text-sm atlas-font-semibold">
              {t("sites.utility_report.estimated_delivery_time")}
            </span>

            <div className="atlas-flex atlas-flex-row atlas-w-full atlas-gap-x-2">
              <i className="icon-lt-clock-line atlas-text-xl atlas-flex atlas-pt-1" />
              <div className="atlas-flex atlas-flex-col atlas-gap-y-1">
                <span className="atlas-flex atlas-text-xl atlas-font-semibold">
                  {t("sites.utility_report.10_working_days")}
                </span>
                <span className="atlas-text-sm atlas-leading-6">
                  {t(
                    "sites.utility_report.all_utilities_data_sets_will_be_supplied_within_10_working_days",
                    {
                      contact: (
                        <Button.Base
                          buttonType="Anchor"
                          className="atlas-m-0 atlas-p-0 atlas-text-sm atlas-leading-6 atlas-text-content-link atlas-font-semibold atlas-h-6"
                          onClick={onContactClick}
                        >
                          {t("sites.utility_report.contact_us")}
                        </Button.Base>
                      ),
                    }
                  )}
                </span>
                <span className="atlas-text-sm atlas-leading-6">
                  {t("sites.utility_report.once_available_you_will_receive_an_email")}
                </span>
              </div>
            </div>
          </div>
          <div className="atlas-p-3 atlas-flex atlas-flex-row atlas-gap-x-3 atlas-w-full atlas-justify-end">
            <Button.Secondary
              size="small"
              onClick={onCancelClick}
              className="atlas-border-none"
              disabled={loading}
            >
              {t("sites.utility_report.cancel")}
            </Button.Secondary>
            <Button.Primary size="small" onClick={onConfirmPayment} disabled={loading}>
              {t("sites.utility_report.confirm")}
            </Button.Primary>
          </div>
        </>
      )}
    </Modal>
  );
};
